import React, { useState, useEffect } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { cn } from 'src/enviroment/lib/utils'
import Typography from '../atoms/Typography'
import { ErrorMessage } from '../atoms/ErrorMessage'
import { api } from '../../api/root'
import { Dataset } from '../../types/dataset'

interface DatasetSelectorProps {
  onDatasetChange: (datasetId: string | null) => void
  label?: string
  errorMessage?: string
  placeholder?: string
  className?: string
  defaultValue?: Dataset | null
}

const DatasetSelector = ({
  onDatasetChange,
  label,
  errorMessage,
  className,
  placeholder = 'Search dataset',
  defaultValue,
}: DatasetSelectorProps) => {
  const [inputValue, setInputValue] = useState('')
  const [selectedDataset, setSelectedDataset] = useState<Dataset | null>(defaultValue || null)
  const [isFocused, setIsFocused] = useState(false)

  const { data: datasets = [], isLoading } = api.dataset.list.useQuery()

  const createDatasetMutation = api.dataset.create.useMutation()
  const deleteDatasetMutation = api.dataset.delete.useMutation()

  const filtered = datasets.filter((d) => d.name.toLowerCase().includes(inputValue.trim().toLowerCase()))
  const exactMatch = datasets.some((d) => d.name.toLowerCase() === inputValue.trim().toLowerCase())

  useEffect(() => {
    setSelectedDataset(defaultValue || null)
  }, [defaultValue])

  const handleSelect = (dataset: Dataset) => {
    setSelectedDataset(dataset)
    onDatasetChange(dataset.id)
    setInputValue('')
  }

  const handleDelete = async (datasetId: string) => {
    try {
      await deleteDatasetMutation.mutateAsync(datasetId)
      if (selectedDataset?.id === datasetId) {
        setSelectedDataset(null)
        onDatasetChange(null)
      }
      setInputValue('')
    } catch (error) {
      console.error('Error deleting dataset:', error)
    }
  }

  const handleClearSelection = () => {
    setSelectedDataset(null)
    onDatasetChange(null)
    setInputValue('')
  }

  const handleCreate = async (name: string) => {
    if (!name.trim()) return
    try {
      const newDataset = await createDatasetMutation.mutateAsync(name.trim())
      handleSelect(newDataset)
    } catch (error) {
      console.error('Error creating dataset:', error)
    }
  }

  const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (filtered[0]) {
        handleSelect(filtered[0])
      } else if (inputValue.trim() && !exactMatch) {
        await handleCreate(inputValue)
      }
    }
    if (e.key === 'Backspace' && !inputValue && selectedDataset) {
      e.preventDefault()
      handleClearSelection()
    }
  }

  return (
    <div className="space-y-2">
      {label && (
        <Typography variant="sm-medium" element="label" className="block text-gray-50">
          {label}
        </Typography>
      )}
      <div
        className={cn(
          'relative flex h-[48px] w-full items-center rounded-lg bg-gray-800 px-4',
          'border border-gray-300',
          'transition-all duration-200',
          'focus-within:border-light-green-500 focus-within:shadow-[0_0_0_1px_rgba(50,145,117,0.3)] hover:border-gray-400',
          errorMessage &&
            'border-red-ramo focus-within:border-red-ramo focus-within:shadow-[0_0_0_1px_rgba(215,27,61,0.3)]',
          className,
        )}
      >
        {selectedDataset ? (
          <div className="flex w-full items-center justify-between">
            <span className="text-sm-medium text-gray-50">{selectedDataset.name}</span>
            <button
              type="button"
              onClick={handleClearSelection}
              className="grid h-6 w-6 place-items-center rounded-full text-gray-300
                transition-colors hover:bg-gray-700 hover:text-white"
            >
              ×
            </button>
          </div>
        ) : (
          <input
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setTimeout(() => setIsFocused(false), 200)}
            className="flex h-full w-full items-center bg-transparent text-base text-gray-50 outline-none placeholder:text-gray-300"
            placeholder={placeholder}
          />
        )}

        {inputValue && !isLoading && isFocused && !selectedDataset && (
          <div className="absolute left-0 right-0 top-[48px] z-50 overflow-hidden rounded-lg border border-gray-300 bg-gray-800 shadow-lg">
            {filtered.map((dataset) => (
              <div
                key={dataset.id}
                className="flex items-center justify-between p-3 text-sm text-gray-50 transition-colors hover:bg-gray-700"
              >
                <button type="button" className="flex-1 text-left" onClick={() => handleSelect(dataset)}>
                  {dataset.name}
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(dataset.id)}
                  className="ml-2 p-1 text-gray-300 hover:text-red-500"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
            {inputValue.trim() && !exactMatch && (
              <button
                type="button"
                className="flex w-full items-center gap-2 border-t border-gray-700 p-3 text-sm text-gray-50 transition-colors hover:bg-gray-700"
                onClick={() => handleCreate(inputValue)}
              >
                <Plus className="h-4 w-4 text-light-green-500" />
                Create &quot;{inputValue.trim()}&quot;
              </button>
            )}
          </div>
        )}
      </div>
      {errorMessage && <ErrorMessage message={errorMessage} />}
    </div>
  )
}

export default DatasetSelector
